export type AttachmentChannel = 'whatsapp' | 'instagram' | 'facebook' | 'teamchat'
export type AttachmentKind = 'image' | 'video' | 'audio' | 'document'
export type StagedMediaKind = AttachmentKind | 'sticker'

export interface AttachmentRule {
  kind: StagedMediaKind
  label: string
  formats: string[]
  mimes: string[]
  extensions: string[]
  maxBytes: number
  multiple: boolean
  notes: string[]
}

export const MAX_ATTACHMENTS_PER_MESSAGE = 10

const MB = 1024 * 1024
const KB = 1024

export const SOCIAL_DIRECT_UPLOAD_MAX_BYTES = 10 * MB
const FACEBOOK_UPLOAD_MAX_BYTES = SOCIAL_DIRECT_UPLOAD_MAX_BYTES
const FACEBOOK_URL_IMAGE_MAX_BYTES = 8 * MB
export const STATIC_STICKER_MAX_BYTES = 100 * KB
export const ANIMATED_STICKER_MAX_BYTES = 500 * KB

const MIME_ALIASES: Record<string, string> = {
  'image/jpg': 'image/jpeg',
  'image/pjpeg': 'image/jpeg',
  'audio/mp3': 'audio/mpeg',
  'audio/x-mp3': 'audio/mpeg',
  'audio/x-mpeg': 'audio/mpeg',
  'audio/x-m4a': 'audio/mp4',
  'audio/m4a': 'audio/mp4',
  'audio/x-aac': 'audio/aac',
  'audio/x-wav': 'audio/wav',
  'audio/wave': 'audio/wav',
  'video/x-m4v': 'video/mp4',
}

export function canonicalMimeType(mime: string | null | undefined): string {
  const m = String(mime || '').trim().toLowerCase()
  return MIME_ALIASES[m] || m
}

const DOCUMENT_MIMES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
]
const DOCUMENT_EXTENSIONS = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt']

const WHATSAPP: Record<StagedMediaKind, AttachmentRule> = {
  image: {
    kind: 'image', label: 'Image', formats: ['JPEG (.jpg, .jpeg)', 'PNG'],
    mimes: ['image/jpeg', 'image/png'], extensions: ['jpg', 'jpeg', 'png'],
    maxBytes: 5 * MB, multiple: true,
    notes: ['Images must be 8-bit, RGB or RGBA.'],
  },
  video: {
    kind: 'video', label: 'Video', formats: ['MP4', '3GP'],
    mimes: ['video/mp4', 'video/3gpp'], extensions: ['mp4', '3gp'],
    maxBytes: 16 * MB, multiple: true,
    notes: ['Only the H.264 video codec and AAC audio codec are supported, with a single audio stream or none.'],
  },
  audio: {
    kind: 'audio', label: 'Audio', formats: ['AAC', 'AMR', 'MP3', 'M4A', 'OGG'],
    mimes: ['audio/aac', 'audio/amr', 'audio/mpeg', 'audio/mp4', 'audio/ogg'],
    extensions: ['aac', 'amr', 'mp3', 'm4a', 'ogg'],
    maxBytes: 16 * MB, multiple: true,
    notes: ['OGG audio must use the Opus codec and be mono.'],
  },
  document: {
    kind: 'document', label: 'Document', formats: ['PDF', 'DOC', 'DOCX', 'XLS', 'XLSX', 'PPT', 'PPTX', 'TXT'],
    mimes: DOCUMENT_MIMES, extensions: DOCUMENT_EXTENSIONS,
    maxBytes: 100 * MB, multiple: true,
    notes: [],
  },
  sticker: {
    kind: 'sticker', label: 'Sticker', formats: ['WEBP'],
    mimes: ['image/webp'], extensions: ['webp'],
    maxBytes: ANIMATED_STICKER_MAX_BYTES, multiple: true,
    notes: ['WebP files are sent as stickers: 100 KB static, 500 KB animated.'],
  },
}

const INSTAGRAM: Partial<Record<StagedMediaKind, AttachmentRule>> = {
  image: {
    kind: 'image', label: 'Image', formats: ['JPEG (.jpg, .jpeg)', 'PNG'],
    mimes: ['image/jpeg', 'image/png'], extensions: ['jpg', 'jpeg', 'png'],
    maxBytes: 8 * MB, multiple: true,
    notes: [],
  },
  video: {
    kind: 'video', label: 'Video', formats: ['MP4', 'MOV', 'WEBM', 'AVI', 'OGG'],
    mimes: ['video/mp4', 'video/quicktime', 'video/webm', 'video/x-msvideo', 'video/avi', 'video/ogg'],
    extensions: ['mp4', 'mov', 'webm', 'avi', 'ogv'],
    maxBytes: 25 * MB, multiple: false,
    notes: [],
  },
  document: {
    kind: 'document', label: 'Document', formats: ['PDF'],
    mimes: ['application/pdf'], extensions: ['pdf'],
    maxBytes: 25 * MB, multiple: false,
    notes: ['Instagram only accepts PDF documents.'],
  },
}

const FACEBOOK: Partial<Record<StagedMediaKind, AttachmentRule>> = {
  image: {
    kind: 'image', label: 'Image', formats: ['JPEG (.jpg, .jpeg)', 'PNG', 'GIF'],
    mimes: ['image/jpeg', 'image/png', 'image/gif'], extensions: ['jpg', 'jpeg', 'png', 'gif'],
    maxBytes: FACEBOOK_URL_IMAGE_MAX_BYTES, multiple: true,
    notes: ['Messenger accepts images up to 8 MB when they are sent by link.'],
  },
  video: {
    kind: 'video', label: 'Video', formats: ['MP4', 'MOV'],
    mimes: ['video/mp4', 'video/quicktime'], extensions: ['mp4', 'mov'],
    maxBytes: FACEBOOK_UPLOAD_MAX_BYTES, multiple: false,
    notes: ['Messenger allows up to 25 MB; uploads through WeNext are currently limited to 10 MB.'],
  },
  audio: {
    kind: 'audio', label: 'Audio', formats: ['MP3', 'M4A', 'AAC', 'WAV'],
    mimes: ['audio/mpeg', 'audio/mp4', 'audio/aac', 'audio/wav'],
    extensions: ['mp3', 'm4a', 'aac', 'wav'],
    maxBytes: FACEBOOK_UPLOAD_MAX_BYTES, multiple: false,
    notes: ['Messenger allows up to 25 MB; uploads through WeNext are currently limited to 10 MB.'],
  },
  document: {
    kind: 'document', label: 'Document', formats: ['PDF', 'DOC', 'DOCX', 'XLS', 'XLSX', 'PPT', 'PPTX', 'TXT'],
    mimes: DOCUMENT_MIMES, extensions: DOCUMENT_EXTENSIONS,
    maxBytes: FACEBOOK_UPLOAD_MAX_BYTES, multiple: false,
    notes: ['Messenger allows up to 25 MB; uploads through WeNext are currently limited to 10 MB.'],
  },
}

/*
 * Team chat (team channel + direct messages between team members). Files go
 * straight to S3, so no channel limits apply - only WeNext's own. Must match
 * backend/src/domain/chat/teamChatAttachments.js, which checks again. Only
 * these types: programs and scripts (.exe, .bat, .msi, …) are not on it.
 */
const TEAM_CHAT: Partial<Record<StagedMediaKind, AttachmentRule>> = {
  image: {
    kind: 'image', label: 'Image', formats: ['JPEG (.jpg, .jpeg)', 'PNG', 'GIF', 'WEBP'],
    mimes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'], extensions: ['jpg', 'jpeg', 'png', 'gif', 'webp'],
    maxBytes: 10 * MB, multiple: true,
    notes: [],
  },
  video: {
    kind: 'video', label: 'Video', formats: ['MP4', 'MOV', 'WEBM'],
    mimes: ['video/mp4', 'video/quicktime', 'video/webm'], extensions: ['mp4', 'mov', 'webm'],
    maxBytes: 50 * MB, multiple: true,
    notes: [],
  },
  audio: {
    kind: 'audio', label: 'Audio', formats: ['MP3', 'M4A', 'AAC', 'WAV', 'OGG'],
    mimes: ['audio/mpeg', 'audio/mp4', 'audio/aac', 'audio/wav', 'audio/ogg', 'audio/webm'],
    extensions: ['mp3', 'm4a', 'aac', 'wav', 'ogg', 'weba'],
    maxBytes: 16 * MB, multiple: true,
    notes: [],
  },
  document: {
    kind: 'document', label: 'Document', formats: ['PDF', 'DOC', 'DOCX', 'XLS', 'XLSX', 'PPT', 'PPTX', 'TXT', 'CSV'],
    mimes: [...DOCUMENT_MIMES, 'text/csv'], extensions: [...DOCUMENT_EXTENSIONS, 'csv'],
    maxBytes: 50 * MB, multiple: true,
    notes: [],
  },
}

const RULES: Record<AttachmentChannel, Partial<Record<StagedMediaKind, AttachmentRule>>> = {
  whatsapp: WHATSAPP,
  instagram: INSTAGRAM,
  facebook: FACEBOOK,
  teamchat: TEAM_CHAT,
}

export function attachmentRule(channel: AttachmentChannel, kind: StagedMediaKind): AttachmentRule | null {
  return RULES[channel]?.[kind] ?? null
}

export function availableAttachmentKinds(channel: AttachmentChannel): AttachmentKind[] {
  return (['image', 'video', 'audio', 'document'] as AttachmentKind[]).filter((k) => !!RULES[channel]?.[k])
}

export function fileExtension(name: string | undefined | null): string {
  const n = String(name || '')
  const dot = n.lastIndexOf('.')
  return dot >= 0 ? n.slice(dot + 1).toLowerCase() : ''
}

function matchesRule(file: File, rule: AttachmentRule, channel?: AttachmentChannel): boolean {
  const mime = canonicalMimeType(file.type)
  // Team chat: the extension must be allowed AND the type must match it (or be
  // unknown) - the same rule the API applies (teamChatAttachments.js), so a
  // file accepted here is never refused after the upload has started.
  if (channel === 'teamchat') {
    if (!rule.extensions.includes(fileExtension(file.name))) return false
    return !mime || mime === 'application/octet-stream' || rule.mimes.includes(mime)
  }
  if (mime && rule.mimes.includes(mime)) return true
  return rule.extensions.includes(fileExtension(file.name))
}

export function detectAttachmentKind(file: File, channel: AttachmentChannel): StagedMediaKind | null {
  const kinds: StagedMediaKind[] = ['image', 'sticker', 'video', 'audio', 'document']
  for (const kind of kinds) {
    const rule = attachmentRule(channel, kind)
    if (rule && matchesRule(file, rule, channel)) return kind
  }
  return null
}

function knownUnsupportedReason(file: File, channel: AttachmentChannel, expected: AttachmentKind): string | null {
  const ext = fileExtension(file.name)
  const mime = canonicalMimeType(file.type)
  const api = channel === 'whatsapp' ? 'the WhatsApp Business API' : CHANNEL_NAME[channel]
  if (expected === 'image' && (['heic', 'heif'].includes(ext) || mime === 'image/heic' || mime === 'image/heif')) {
    return `HEIC/HEIF photos aren’t supported by ${api} — convert the photo to JPEG first.`
  }
  if (channel === 'whatsapp' && expected === 'video' && (ext === 'mov' || mime === 'video/quicktime')) {
    return 'MOV isn’t supported by the WhatsApp Business API — export the video as MP4 (H.264 video, AAC audio).'
  }
  return null
}

export function formatBytes(bytes: number): string {
  if (bytes < MB) return `${Math.max(1, Math.round(bytes / KB))} KB`
  const mb = bytes / MB
  return `${mb >= 10 ? Math.round(mb) : Number(mb.toFixed(1))} MB`
}

const CHANNEL_NAME: Record<AttachmentChannel, string> = {
  whatsapp: 'WhatsApp',
  instagram: 'Instagram',
  facebook: 'Facebook',
  teamchat: 'team chat',
}

export interface AttachmentCheck {
  ok: boolean
  reason: string | null
  kind: StagedMediaKind | null
  rule: AttachmentRule | null
}

export function checkAttachment(file: File, channel: AttachmentChannel, expected?: AttachmentKind): AttachmentCheck {
  if (!file || file.size === 0) return { ok: false, reason: 'This file is empty.', kind: null, rule: null }
  const ext = fileExtension(file.name)
  const shown = ext ? ext.toUpperCase() : file.type || 'This file type'

  if (expected) {
    const rule = attachmentRule(channel, expected)
    if (!rule) return { ok: false, reason: `${CHANNEL_NAME[channel]} can't send ${expected} files here.`, kind: null, rule: null }
    const stickerRule = channel === 'whatsapp' && expected === 'image' ? attachmentRule(channel, 'sticker') : null
    if (!matchesRule(file, rule, channel)) {
      if (stickerRule && matchesRule(file, stickerRule)) {
        return { ok: false, reason: 'WebP can only be sent as a sticker on WhatsApp — use JPEG or PNG for images.', kind: null, rule: null }
      }
      const known = knownUnsupportedReason(file, channel, expected)
      if (known) return { ok: false, reason: known, kind: null, rule: null }
      return { ok: false, reason: `${shown} isn’t supported — use ${rule.formats.join(', ')}.`, kind: null, rule: null }
    }
    if (file.size > rule.maxBytes) {
      return { ok: false, reason: `${formatBytes(file.size)} — ${rule.label.toLowerCase()}s must be ${formatBytes(rule.maxBytes)} or less.`, kind: null, rule: null }
    }
    return { ok: true, reason: null, kind: rule.kind, rule }
  }

  const kind = detectAttachmentKind(file, channel)
  const rule = kind ? attachmentRule(channel, kind) : null
  if (!kind || !rule) {
    return { ok: false, reason: `${shown} files can’t be sent on ${CHANNEL_NAME[channel]}.`, kind: null, rule: null }
  }
  if (file.size > rule.maxBytes) {
    return { ok: false, reason: `${formatBytes(file.size)} — ${rule.label.toLowerCase()}s must be ${formatBytes(rule.maxBytes)} or less.`, kind: null, rule: null }
  }
  return { ok: true, reason: null, kind, rule }
}

const MIME_BY_EXTENSION: Record<string, string> = {
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  txt: 'text/plain',
  mp3: 'audio/mpeg',
  m4a: 'audio/mp4',
  aac: 'audio/aac',
  wav: 'audio/wav',
  mp4: 'video/mp4',
  mov: 'video/quicktime',
  webm: 'video/webm',
  gif: 'image/gif',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  amr: 'audio/amr',
  ogg: 'audio/ogg',
  '3gp': 'video/3gpp',
  avi: 'video/x-msvideo',
  ogv: 'video/ogg',
}

export function withResolvedMimeType(file: File): File {
  const canonical = canonicalMimeType(file.type)
  const resolved = canonical || MIME_BY_EXTENSION[fileExtension(file.name)] || ''
  if (!resolved || resolved === file.type) return file
  return new File([file], file.name, { type: resolved, lastModified: file.lastModified })
}

export async function isAnimatedWebp(file: Blob): Promise<boolean> {
  try {
    const head = new Uint8Array(await file.slice(0, 64).arrayBuffer())
    const tag = (at: number) => String.fromCharCode(head[at], head[at + 1], head[at + 2], head[at + 3])
    if (tag(0) !== 'RIFF' || tag(8) !== 'WEBP') return false
    if (tag(12) !== 'VP8X') return false
    return (head[20] & 0x02) === 0x02
  } catch {
    return false
  }
}

export async function checkStickerSize(file: File): Promise<string | null> {
  if (file.size <= STATIC_STICKER_MAX_BYTES) return null
  if (file.size > ANIMATED_STICKER_MAX_BYTES) {
    return `${formatBytes(file.size)} — stickers must be ${formatBytes(ANIMATED_STICKER_MAX_BYTES)} or less (animated) or ${formatBytes(STATIC_STICKER_MAX_BYTES)} (static).`
  }
  return (await isAnimatedWebp(file))
    ? null
    : `${formatBytes(file.size)} — static stickers must be ${formatBytes(STATIC_STICKER_MAX_BYTES)} or less.`
}

export function acceptAttribute(rule: AttachmentRule): string {
  return [...rule.mimes, ...rule.extensions.map((e) => `.${e}`)].join(',')
}
